'use client';
import type { ReactNode } from 'react';
import cn from 'classnames';
import { IconNotificationOutline } from '@/assets/icons/IconNotificationOutline';
import { TrianglePointIcon } from '@/assets/icons/TrianglePointIcon';
import styles from './flexibleFormAlert.module.scss';

export type FlexibleFormAlertTone = 'info' | 'success' | 'warning' | 'critical' | 'offer';

interface FlexibleFormAlertProps {
    tone: FlexibleFormAlertTone;
    message: ReactNode;
    leadIn?: ReactNode;
    className?: string;
}

const toneClassByTone: Record<FlexibleFormAlertTone, string> = {
    info: styles.toneInfo,
    success: styles.toneSuccess,
    warning: styles.toneWarning,
    critical: styles.toneCritical,
    offer: styles.toneOffer,
};

function SuccessCircleIcon(): React.JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="10" cy="10" r="8.75" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M6 10.25L8.5 12.75L14 7.25" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}

function WarningCircleIcon(): React.JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="10" cy="10" r="8.75" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M10.5 12.5H9.5L9 9V5H11V9L10.5 12.5Z" fill="currentColor" />
            <path d="M9 14H11V16H9V14Z" fill="currentColor" />
        </svg>
    );
}

function OfferTagIcon(): React.JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
                d="M2.5 7.5V3.5H6.5L15 12L11 16L2.5 7.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
            />
            <circle cx="5.5" cy="5.5" r="1.1" fill="currentColor" />
        </svg>
    );
}

const iconByTone: Record<FlexibleFormAlertTone, () => React.JSX.Element> = {
    info: () => <IconNotificationOutline className={styles.iconSvg} />,
    success: () => <SuccessCircleIcon />,
    warning: () => <WarningCircleIcon />,
    critical: () => <TrianglePointIcon />,
    offer: () => <OfferTagIcon />,
};

function AlertIcon({ tone }: { tone: FlexibleFormAlertTone }) {
    const Icon = iconByTone[tone];
    return <Icon />;
}

export function FlexibleFormAlert({ tone, message, leadIn, className }: FlexibleFormAlertProps): React.JSX.Element {
    const wrapperClassName = cn(styles.alert, toneClassByTone[tone], className);

    return (
        <div className={wrapperClassName} role="alert">
            <div className={styles.leadingIcon}>
                <AlertIcon tone={tone} />
            </div>
            <div className={styles.content}>
                {leadIn ? <div className={styles.leadIn}>{leadIn}</div> : null}
                <div className={styles.message}>{message}</div>
            </div>
        </div>
    );
}
